// =============================================================================
// accessLockout.ts — configurable, role-group login lockout
// =============================================================================
// A SuperUser can lock whole role groups out of the portal (Manage → System
// Setup → Access) — e.g. while the platform is having issues. A locked-out user
// is refused at login AND on session restore, and shown a configurable message.
//
// Stored as ONE portal_config key (`access_lockout`) whose value is:
//   { shelter, picker, responder, supervisor, message }
// (all four booleans default false; message falls back to the default below).
//
// SAFETY: SuperUsers are NEVER locked out — otherwise the person holding the
// switches could lock themselves (and everyone) out with no way back in.
//
// This module is PURE (no I/O) so it can be unit-tested and reused by both the
// login gate (Login.finishLogin) and the session-restore gate (portal.jsx).
// =============================================================================

import { ROLES } from "../utils/enums";

export const ACCESS_LOCKOUT_KEY = "access_lockout";

export const DEFAULT_ACCESS_LOCKOUT_MESSAGE =
  "The Portal is experiencing issues currently and Access is not possible. Try back later";

export interface AccessLockout {
  // Lockout all DFV Services (role `shelter`).
  shelter: boolean;
  // Lockout all Zephyr Pickers (role `picker`).
  picker: boolean;
  // Lockout all Zephyr Responders (role `staff`).
  responder: boolean;
  // Lockout all Zephyr Supervisors (role `admin`, the "Supervisor" tier).
  supervisor: boolean;
  // The message shown to a locked-out user at the login screen.
  message: string;
}

// Coerce any stored/absent value into a well-formed AccessLockout. A missing key
// (or malformed value) yields "all off + default message" — the default state.
export function normalizeAccessLockout(value: unknown): AccessLockout {
  const v = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const rawMsg = typeof v.message === "string" ? v.message : "";
  return {
    shelter: v.shelter === true,
    picker: v.picker === true,
    responder: v.responder === true,
    supervisor: v.supervisor === true,
    message: rawMsg.trim() ? rawMsg : DEFAULT_ACCESS_LOCKOUT_MESSAGE,
  };
}

// Is a user with this role currently locked out?
//  - SuperUser: NEVER (so the switches can always be reversed).
//  - shelter / picker / staff / admin: their own switch.
//  - combined (Picker + Responder): locked if EITHER the picker OR responder
//    switch is on (a combined user does both jobs — John, 25 Jul 2026).
//  - any other / unknown role: not locked (fail open — a lockout must be explicit).
export function isRoleLockedOut(role: string | null | undefined, lockout: AccessLockout): boolean {
  switch (role) {
    case ROLES.SUPERUSER:
      return false;
    case ROLES.SHELTER:
      return lockout.shelter;
    case ROLES.PICKER:
      return lockout.picker;
    case ROLES.STAFF:
      return lockout.responder;
    case ROLES.ADMIN:
      return lockout.supervisor;
    case ROLES.COMBINED:
      return lockout.picker || lockout.responder;
    default:
      return false;
  }
}
